import dayjs from "dayjs";
import { EditionKind } from "../types";
import {
  MeetingTool,
  PersonRef,
  PhysicalSessionDraft,
  SessionDraft,
  VirtualSessionDraft,
} from "./types";

export const personLabel = (it: any): string =>
  it?.label ||
  [it?.name, it?.lastname ?? it?.surname].filter(Boolean).join(" ").trim() ||
  String(it?.id ?? "");

const toRefs = (items?: any): PersonRef[] =>
  Array.isArray(items)
    ? items
        .filter((it) => it && it.id != null)
        .map((it) => ({ id: it.id, label: personLabel(it) }))
    : [];

const toIso = (value?: any): string | null =>
  value ? dayjs(value).toISOString() : null;

const sessionToVirtualDraft = (s: any): VirtualSessionDraft => ({
  id: s?.id,
  start_date: toIso(s?.start_date),
  end_date: toIso(s?.end_date),
  faculties: toRefs(s?.faculties),
  tutors: toRefs(s?.tutors),
  tool: (s?.tool as MeetingTool) || null,
  producer: toRefs(s?.producer),
  presenter: toRefs(s?.presenter),
  coorganizer: toRefs(s?.coorganizer ?? s?.co_organizer),
  is_dial_in_bypass_enabled: s?.is_dial_in_bypass_enabled === true,
  scope: s?.scope || null,
  allow_meeting_chat: s?.allow_meeting_chat || null,
  allow_attendee_to_enable_camera: s?.allow_attendee_to_enable_camera === true,
  allow_attendee_to_enable_mic: s?.allow_attendee_to_enable_mic === true,
  allow_breakout_rooms: s?.allow_breakout_rooms === true,
  allow_recording: s?.allow_recording === true,
  allow_transcription: s?.allow_transcription === true,
  record_automatically: s?.record_automatically === true,
  allowed_presenters: s?.allowed_presenters || null,
  cohost: toRefs(s?.cohost),
  access_type: s?.access_type || null,
  chat_restriction: s?.chat_restriction || null,
  default_join_as_viewer_type: s?.default_join_as_viewer_type === true,
  auto_transcription_generation: s?.auto_transcription_generation === true,
  auto_recording_generation: s?.auto_recording_generation === true,
  link: s?.link || null,
  confirmed: s?.confirmed === true,
  trigger_external_services:
    !!s?.online_meeting_updated_at || s?.trigger_external_services === true,
  online_meeting_updated_at: s?.online_meeting_updated_at ?? null,
});

const sessionToPhysicalDraft = (s: any): PhysicalSessionDraft => ({
  id: s?.id,
  start_date: toIso(s?.start_date),
  end_date: toIso(s?.end_date),
  faculties: toRefs(s?.faculties),
  tutors: toRefs(s?.tutors),
  site: s?.site
    ? Array.isArray(s.site)
      ? toRefs(s.site)[0] || null
      : { id: s.site.id, label: s.site.label ?? String(s.site.id) }
    : null,
  rooms: toRefs(s?.rooms),
  qrCode: s?.["QR-code"] === true || s?.qrCode === true,
  confirmed: s?.confirmed === true,
});

export const sessionToDraft = (
  session: any,
  kind: EditionKind,
): SessionDraft =>
  kind === "virtual"
    ? sessionToVirtualDraft(session)
    : sessionToPhysicalDraft(session);

export const computeDisabledFields = (
  session: any,
  kind: EditionKind,
  publishDate?: string | number | null,
  allEnrollmentsCompleted?: boolean,
): Set<string> => {
  const disabled = new Set<string>();
  if (!session) return disabled;

  const now = dayjs();
  const editionPublished = publishDate
    ? now.isAfter(dayjs(publishDate))
    : false;
  const beforePublish = publishDate ? now.isBefore(dayjs(publishDate)) : false;

  const startDate = session.start_date ? dayjs(session.start_date) : null;
  const endDate = session.end_date ? dayjs(session.end_date) : null;

  const sessionEnded = !!endDate && now.isAfter(endDate) && editionPublished;
  const sessionStarted =
    !!startDate && now.isAfter(startDate) && editionPublished;
  const startsWithin24h =
    !!startDate &&
    now.isBefore(startDate) &&
    startDate.diff(now, "hour", true) <= 24 &&
    editionPublished;
  const isSessionExpired = !!endDate && now.isAfter(endDate);
  const meetingCreated = !!session.online_meeting_updated_at;
  const sessionConfirmed = session.confirmed === true;
  const enrollmentBlocked = allEnrollmentsCompleted === false;

  const isVirtual = kind === "virtual";
  const physicalTimeLock = !isVirtual && (startsWithin24h || sessionStarted);

  if (sessionEnded || physicalTimeLock) {
    ["tool", "link", "site", "rooms", "QR-code"].forEach((f) =>
      disabled.add(f),
    );
  }

  if (isVirtual && meetingCreated) {
    disabled.add("tool");
    disabled.add("trigger_external_services");
  }

  if (isVirtual && (beforePublish || isSessionExpired)) {
    disabled.add("trigger_external_services");
  }

  const confirmedDisabled =
    sessionConfirmed ||
    isSessionExpired ||
    enrollmentBlocked ||
    (beforePublish && (!isVirtual || session.tool === "OTHER"));
  if (confirmedDisabled) disabled.add("confirmed");

  return disabled;
};

export const computeCreateDisabledFields = (
  kind: EditionKind,
  tool: string | null | undefined,
  publishDate?: string | number | null,
): Set<string> => {
  const disabled = new Set<string>();
  const beforePublish = publishDate
    ? dayjs().isBefore(dayjs(publishDate))
    : false;
  if (!beforePublish) return disabled;

  if (kind === "virtual") disabled.add("trigger_external_services");
  if (kind === "physical" || tool === "OTHER") disabled.add("confirmed");

  return disabled;
};
